import React, { useCallback } from 'react';
import { QButtonGroup, QButton } from '@qualio/ui-components';
import { useTranslation } from 'react-i18next';
import { User } from '../../../types';

type StateSetterFunction<T> = React.Dispatch<React.SetStateAction<T>>;

type UserActionButtonsProps = {
  areUsersSelected: boolean;
  currentlySelectedUsers: User[];
  setInviteModalOpen: StateSetterFunction<boolean>;
  setRoleModalOpen: StateSetterFunction<boolean>;
  setAddGroupModalOpen: StateSetterFunction<boolean>;
  setRemoveUserGroupsModalOpen: StateSetterFunction<boolean>;
};

const doesUserHaveGroups = (selectedUsers: User[]) => selectedUsers.some((user) => user.groups.length > 0);

export const UserActionButtons = ({
  currentlySelectedUsers,
  areUsersSelected,
  setRoleModalOpen,
  setAddGroupModalOpen,
  setRemoveUserGroupsModalOpen,
  setInviteModalOpen,
}: UserActionButtonsProps) => {
  const { t } = useTranslation();

  const openRoleModal = useCallback(() => setRoleModalOpen(true), []);
  const openAddUserGroupModal = useCallback(() => setAddGroupModalOpen(true), []);
  const openRemoveUserGroupsModal = useCallback(() => setRemoveUserGroupsModalOpen(true), []);
  const openInviteModal = useCallback(() => setInviteModalOpen(true), []);

  const count = currentlySelectedUsers.length;
  const pluralized = count === 1 ? t('common.user') : t('common.users');

  const addToGroupLabel = count < 1 ? t('users.addToGroup') : t('users.addToGroupCount', { count, pluralized });

  const removeFromGroupLabel =
    count < 1 ? t('users.removeFromGroup') : t('users.removeFromGroupCount', { count, pluralized });

  return (
    <QButtonGroup>
      <QButton
        type="button"
        aria-label={t('users.setRole')}
        isDisabled={!areUsersSelected}
        variant="outline"
        data-cy="set-role"
        onClick={openRoleModal}
      >
        {t('users.setRole')}
      </QButton>
      <QButton
        type="button"
        isDisabled={!areUsersSelected}
        variant="outline"
        data-cy="add-to-group"
        aria-label={t('aria.addToGroup')}
        onClick={openAddUserGroupModal}
      >
        {addToGroupLabel}
      </QButton>
      <QButton
        type="button"
        isDisabled={!doesUserHaveGroups(currentlySelectedUsers) || !areUsersSelected}
        variant="outline"
        data-cy="remove-from-group"
        aria-label={t('aria.removeFromGroup')}
        onClick={openRemoveUserGroupsModal}
      >
        {removeFromGroupLabel}
      </QButton>
      <QButton
        data-metrics="um-invite-button"
        type="button"
        aria-label={t('users.inviteUser')}
        data-cy="invite-user"
        data-intercom-target="invite-user"
        onClick={openInviteModal}
      >
        {t('users.inviteUser')}
      </QButton>
    </QButtonGroup>
  );
};
